import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import {
  useGetQuickLinksQuery,
  useGetQuickLinkSettingsQuery,
} from '@/store/quickLinksResourcesApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import QuickLinkUtility from './quick-link.utility';

export const useQuickLinks = (isMobile: boolean) => {
  const authLoaded = useAuthIsLoaded();

  const CUSTOMER_SERVICE_PREFERENCES_URL =
    'https://banking.commercebank.com/CBI/CustomerService/MyProfile';

  const {
    data: quickLinksData,
    isLoading: isQuickLinksDataLoading,
    error: quickLinksDataError,
  } = useGetQuickLinksQuery(authLoaded ? undefined : skipToken);

  const {
    data: quickLinkSettings,
    isLoading: isQuickLinksSettingLoading,
    error: quickLinkSettingsError,
  } = useGetQuickLinkSettingsQuery(authLoaded ? undefined : skipToken);

  // * NOTE: Intentionally not limited items displayed. See: https://wealthaccess.atlassian.net/browse/EUD-890
  const sortedQuickLinks = useMemo(() => {
    if (!quickLinksData?.Records) return [];

    const filteredQuickLinks = isMobile
      ? quickLinksData.Records.filter(
          (link) =>
            QuickLinkUtility.normalizeQuickLinkUrl(link.Url) !==
            QuickLinkUtility.normalizeQuickLinkUrl(
              CUSTOMER_SERVICE_PREFERENCES_URL,
            ),
        )
      : quickLinksData.Records;

    return QuickLinkUtility.sortItems(
      filteredQuickLinks,
      quickLinkSettings?.Sort.Property ?? 'Name',
      quickLinkSettings?.Sort.Direction ?? 'asc',
    );
  }, [isMobile, quickLinksData, quickLinkSettings]);

  const hasLinks = sortedQuickLinks.length > 0;

  return {
    hasLinks,
    isQuickLinksDataLoading,
    isQuickLinksSettingLoading,
    quickLinksData,
    quickLinksDataError,
    quickLinkSettings,
    sortedQuickLinks,
    quickLinkSettingsError,
  };
};
